/**
 * The 2D stand-in. The star, the sun, the network and the places draw
 * through it exactly as on a 2D canvas, and it turns their paths, strokes,
 * circles, gradients and sprites into batched triangles. Code written against
 * a real 2D context can be pasted in unchanged.
 *
 * It draws in CSS pixels and the renderer maps them onto the device, so
 * `setTransform` resets to the identity whatever it is given: the pixel-ratio
 * scale that 2D code sets up is already taken care of.
 *
 * What it leaves out: line caps and joins (a stroke is always mitred), fill
 * rules (a fill is a fan from each subpath's first point), patterns, and the
 * forms of `drawImage` that crop the source.
 */
import { Batch } from './batch'
import type { BlendMode, Gpu, Viewport } from './gpu'
import { Gradient, parseColor, type Rgba } from './paint'

/** The part of the 2D context the stand-in provides: exactly what its callers use. */
export type Canvas2D = Pick<
  CanvasRenderingContext2D,
  | 'fillStyle'
  | 'strokeStyle'
  | 'lineWidth'
  | 'lineCap'
  | 'lineJoin'
  | 'globalAlpha'
  | 'globalCompositeOperation'
  | 'setTransform'
  | 'save'
  | 'restore'
  | 'translate'
  | 'scale'
  | 'rotate'
  | 'beginPath'
  | 'moveTo'
  | 'lineTo'
  | 'arc'
  | 'ellipse'
  | 'fill'
  | 'stroke'
  | 'fillRect'
  | 'createLinearGradient'
  | 'createRadialGradient'
  | 'drawImage'
>

const QUAD = [
  [-1, -1],
  [1, -1],
  [-1, 1],
  [-1, 1],
  [1, -1],
  [1, 1],
]
const UNIT_QUAD = [
  [0, 0],
  [1, 0],
  [0, 1],
  [0, 1],
  [1, 0],
  [1, 1],
]

type Matrix = [a: number, b: number, c: number, d: number, e: number, f: number]
type Style = string | Gradient
interface Subpath {
  /** Screen points, x and y interleaved. */
  readonly points: number[]
  /** Written when the path is closed. */
  closed: boolean
}
/** A whole circle: its centre on screen, radius, and the transform's x and y scale. */
type Circle = [x: number, y: number, r: number, sx: number, sy: number]
type Saved = [Matrix, number, GlobalCompositeOperation, Style, Style, number]

const scratch: Rgba = [0, 0, 0, 0]

export class StandIn implements Canvas2D {
  fillStyle: string | CanvasGradient | CanvasPattern = '#000'
  strokeStyle: string | CanvasGradient | CanvasPattern = '#000'
  lineWidth = 1
  lineCap: CanvasLineCap = 'butt'
  lineJoin: CanvasLineJoin = 'miter'
  globalAlpha = 1
  globalCompositeOperation: GlobalCompositeOperation = 'source-over'

  private m: Matrix = [1, 0, 0, 1, 0, 0]
  private stack: Saved[] = []
  private subs: Subpath[] = []
  private sub: Subpath | null = null
  private circles: Circle[] = []
  private viewport: Viewport = { width: 0, height: 0, dpr: 1 }
  private readonly batch: Batch

  constructor(
    /** Whether the GPU can draw; batches are dropped while it cannot. */
    ready: () => boolean
  ) {
    this.batch = new Batch(ready)
  }

  /** Builds its GPU resources on this context: once, and again after it is restored. */
  rebuild(gpu: Gpu) {
    this.batch.rebuild(gpu)
  }

  /** Starts a frame: the identity transform, opaque, source-over, nothing batched. */
  begin(viewport: Viewport) {
    this.viewport = viewport
    this.setTransform()
    this.globalAlpha = 1
    this.globalCompositeOperation = 'source-over'
    this.batch.begin(viewport)
  }

  /** Draws whatever is batched. */
  flush() {
    this.batch.flush()
  }

  /* ---------- state and transform ---------- */

  setTransform() {
    this.m = [1, 0, 0, 1, 0, 0]
  }

  save() {
    this.stack.push([
      [...this.m],
      this.globalAlpha,
      this.globalCompositeOperation,
      this.fillStyle as Style,
      this.strokeStyle as Style,
      this.lineWidth,
    ])
  }

  restore() {
    const saved = this.stack.pop()
    if (!saved) return
    ;[
      this.m,
      this.globalAlpha,
      this.globalCompositeOperation,
      this.fillStyle,
      this.strokeStyle,
      this.lineWidth,
    ] = saved
  }

  translate(x: number, y: number) {
    const m = this.m
    m[4] += m[0] * x + m[2] * y
    m[5] += m[1] * x + m[3] * y
  }

  scale(x: number, y: number) {
    const m = this.m
    m[0] *= x
    m[1] *= x
    m[2] *= y
    m[3] *= y
  }

  rotate(angle: number) {
    const m = this.m
    const c = Math.cos(angle)
    const s = Math.sin(angle)
    const [a0, b0, c0, d0] = m
    m[0] = a0 * c + c0 * s
    m[1] = b0 * c + d0 * s
    m[2] = c0 * c - a0 * s
    m[3] = d0 * c - b0 * s
  }

  /* ---------- paths ---------- */

  beginPath() {
    this.subs = []
    this.sub = null
    this.circles = []
  }

  moveTo(x: number, y: number) {
    this.sub = { points: this.tx(x, y), closed: false }
    this.subs.push(this.sub)
  }

  lineTo(x: number, y: number) {
    if (!this.sub) this.moveTo(x, y)
    else this.sub.points.push(...this.tx(x, y))
  }

  arc(x: number, y: number, r: number, a0: number, a1: number, ccw?: boolean) {
    let sweep = a1 - a0
    if (!ccw && sweep < 0) sweep = (sweep % (2 * Math.PI)) + 2 * Math.PI
    if (ccw && sweep > 0) sweep = (sweep % (2 * Math.PI)) - 2 * Math.PI
    if (Math.abs(sweep) >= 2 * Math.PI - 1e-3 && (!this.sub || this.sub.points.length <= 2)) {
      // a whole circle: drawn as one, exactly
      if (this.sub && this.sub.points.length <= 2) {
        this.subs.pop()
        this.sub = null
      }
      const m = this.m
      const [cx, cy] = this.tx(x, y)
      const sx = Math.sqrt(m[0] * m[0] + m[1] * m[1])
      const sy = Math.sqrt(m[2] * m[2] + m[3] * m[3])
      this.circles.push([cx, cy, r, sx, sy])
      return
    }
    if (Math.abs(sweep) > 2 * Math.PI) sweep = sweep > 0 ? 2 * Math.PI : -2 * Math.PI
    const n = Math.max(6, Math.min(96, Math.ceil(Math.abs(sweep) * Math.sqrt(Math.max(r, 1)) * 2)))
    for (let i = 0; i <= n; i++) {
      const a = a0 + (sweep * i) / n
      this.lineTo(x + Math.cos(a) * r, y + Math.sin(a) * r)
    }
  }

  ellipse(x: number, y: number, rx: number, ry: number, rotation: number, a0: number, a1: number) {
    const full = Math.abs(a1 - a0) >= 2 * Math.PI - 1e-3
    const n = 48
    const cr = Math.cos(rotation)
    const sr = Math.sin(rotation)
    this.moveTo(
      x + cr * rx * Math.cos(a0) - sr * ry * Math.sin(a0),
      y + sr * rx * Math.cos(a0) + cr * ry * Math.sin(a0)
    )
    for (let i = 1; i <= n; i++) {
      const a = a0 + ((a1 - a0) * i) / n
      const ex = rx * Math.cos(a)
      const ey = ry * Math.sin(a)
      this.lineTo(x + cr * ex - sr * ey, y + sr * ex + cr * ey)
    }
    if (full) this.sub!.closed = true
  }

  fillRect(x: number, y: number, w: number, h: number) {
    const { subs, circles, sub } = this
    this.beginPath()
    this.moveTo(x, y)
    this.lineTo(x + w, y)
    this.lineTo(x + w, y + h)
    this.lineTo(x, y + h)
    this.sub!.closed = true
    this.fill()
    this.subs = subs
    this.circles = circles
    this.sub = sub
  }

  createLinearGradient(x0: number, y0: number, x1: number, y1: number) {
    return new Gradient('linear', [x0, y0, x1, y1])
  }

  createRadialGradient(x0: number, y0: number, r0: number, x1: number, y1: number, r1: number) {
    return new Gradient('radial', [x0, y0, r0, x1, y1, r1])
  }

  /* ---------- drawing ---------- */

  fill() {
    const style = this.fillStyle as Style
    const blend = this.mode()
    for (const circle of this.circles) this.disc(circle, style, 0, blend)
    for (const { points: p } of this.subs) {
      const n = p.length / 2
      if (n < 3) continue
      const f = this.batch.room('path', blend, null, (n - 2) * 3 * 8)
      for (let k = 1; k < n - 1; k++) {
        for (const v of [0, k, k + 1]) {
          const x = p[v * 2]
          const y = p[v * 2 + 1]
          const c = this.colorAt(style, x, y, scratch)
          const o = this.batch.n
          f[o] = x
          f[o + 1] = y
          f.set(c, o + 2)
          f[o + 6] = 0
          f[o + 7] = 1e4
          this.batch.n += 8
        }
      }
    }
  }

  stroke() {
    const style = this.strokeStyle as Style
    const blend = this.mode()
    const halfWidth = (this.lineWidth * this.scaleOf()) / 2
    for (const circle of this.circles) this.disc(circle, style, this.lineWidth / 2, blend)
    for (const { points, closed } of this.subs)
      this.polyline(points, closed, halfWidth, style, blend)
  }

  drawImage(image: CanvasImageSource, x: number, y: number, w?: number, h?: number) {
    const sized = image as HTMLCanvasElement
    const width = w ?? sized.width
    const height = w === undefined ? sized.height : h!
    const texture = this.batch.sprite(image)
    const a = this.globalAlpha
    const f = this.batch.room('sprite', this.mode(), texture, 6 * 8)
    for (const [u, v] of UNIT_QUAD) {
      const [px, py] = this.tx(x + u * width, y + v * height)
      const o = this.batch.n
      f[o] = px
      f[o + 1] = py
      f[o + 2] = u
      f[o + 3] = v
      f[o + 4] = f[o + 5] = f[o + 6] = f[o + 7] = a
      this.batch.n += 8
    }
  }

  /* ---------- the triangles ---------- */

  /** A polyline as one strip, mitred at the joins so nothing overlaps. */
  private polyline(
    points: number[],
    closed: boolean,
    halfWidth: number,
    style: Style,
    blend: BlendMode
  ) {
    const p: number[] = []
    for (let i = 0; i < points.length / 2; i++) {
      const x = points[i * 2]
      const y = points[i * 2 + 1]
      const L = p.length
      if (L && Math.abs(p[L - 2] - x) < 1e-3 && Math.abs(p[L - 1] - y) < 1e-3) continue
      p.push(x, y)
    }
    let n = p.length / 2
    if (
      closed &&
      n > 2 &&
      Math.abs(p[0] - p[(n - 1) * 2]) < 1e-3 &&
      Math.abs(p[1] - p[(n - 1) * 2 + 1]) < 1e-3
    ) {
      p.length -= 2
      n--
    }
    if (n < 2) return
    const segs = closed ? n : n - 1
    const NX: number[] = []
    const NY: number[] = []
    const MX: number[] = []
    const MY: number[] = []
    for (let i = 0; i < segs; i++) {
      const j = (i + 1) % n
      const dx = p[j * 2] - p[i * 2]
      const dy = p[j * 2 + 1] - p[i * 2 + 1]
      const length = Math.sqrt(dx * dx + dy * dy) || 1
      NX.push(-dy / length)
      NY.push(dx / length)
    }
    for (let i = 0; i < n; i++) {
      const a = closed ? (i - 1 + segs) % segs : Math.max(i - 1, 0)
      const b = closed ? i % segs : Math.min(i, segs - 1)
      let mx = NX[a] + NX[b]
      let my = NY[a] + NY[b]
      let ml = Math.sqrt(mx * mx + my * my)
      if (ml < 1e-6) {
        mx = NX[b]
        my = NY[b]
        ml = 1
      }
      mx /= ml
      my /= ml
      const k = 1 / Math.max(mx * NX[b] + my * NY[b], 0.5)
      MX.push(mx * k)
      MY.push(my * k)
    }
    const dpr = this.viewport.dpr
    const hg = Math.max(halfWidth, 0.5 / dpr) + 0.5 / dpr
    const f = this.batch.room('path', blend, null, segs * 6 * 8)
    const cb: Rgba = [0, 0, 0, 0]
    for (let i = 0; i < segs; i++) {
      const j = (i + 1) % n
      const ca = this.colorAt(style, p[i * 2], p[i * 2 + 1], scratch).slice() as Rgba
      this.colorAt(style, p[j * 2], p[j * 2 + 1], cb)
      const corners: [number, number, Rgba][] = [
        [i, -1, ca],
        [j, -1, cb],
        [i, 1, ca],
        [i, 1, ca],
        [j, -1, cb],
        [j, 1, cb],
      ]
      for (const [q, side, c] of corners) {
        const o = this.batch.n
        f[o] = p[q * 2] + MX[q] * hg * side
        f[o + 1] = p[q * 2 + 1] + MY[q] * hg * side
        f.set(c, o + 2)
        f[o + 6] = side * hg
        f[o + 7] = halfWidth
        this.batch.n += 8
      }
    }
  }

  /** A circle (or the ellipse a scale makes of it), filled or as a ring. */
  private disc(circle: Circle, style: Style, ringHalfWidth: number, blend: BlendMode) {
    const [x, y, r, sx, sy] = circle
    let g0 = 0
    let g1 = 0
    const offsets = [0, 0, 0, 0]
    const colors: Rgba[] = []
    if (typeof style !== 'string' && style.type === 'radial' && !ringHalfWidth) {
      // up to four of the gradient's stops, interpolated per pixel
      const stops = style.stops
      const alpha = this.globalAlpha
      g0 = style.geometry[2]
      g1 = style.geometry[5]
      for (let i = 0; i < 4; i++) {
        let stop = stops[Math.min(Math.round((i * (stops.length - 1)) / 3), stops.length - 1)]
        if (stops.length <= 4) stop = stops[Math.min(i, stops.length - 1)]
        offsets[i] = stop ? stop[0] : 1
        colors.push(stop ? (stop[1].map((v) => v * alpha) as Rgba) : [0, 0, 0, 0])
      }
      for (let i = stops.length; i < 4; i++) offsets[i] = 1 + i
    } else {
      const c = this.colorAt(style, x, y, scratch).slice() as Rgba
      for (let i = 0; i < 4; i++) colors.push(c)
    }
    const dpr = this.viewport.dpr
    const R = r + ringHalfWidth
    const ex = R * sx + 2 / dpr
    const ey = R * sy + 2 / dpr
    const f = this.batch.room('disc', blend, null, 6 * 30)
    for (const [qx, qy] of QUAD) {
      const o = this.batch.n
      f[o] = x + qx * ex
      f[o + 1] = y + qy * ey
      f[o + 2] = x
      f[o + 3] = y
      f[o + 4] = 1 / sx
      f[o + 5] = 1 / sy
      f[o + 6] = r
      f[o + 7] = ringHalfWidth
      f[o + 8] = g0
      f[o + 9] = g1
      f.set(offsets, o + 10)
      for (let i = 0; i < 4; i++) f.set(colors[i], o + 14 + i * 4)
      this.batch.n += 30
    }
  }

  /** The colour of a style at a point on screen, premultiplied. */
  private colorAt(style: Style, x: number, y: number, out: Rgba): Rgba {
    const alpha = this.globalAlpha
    if (typeof style === 'string') {
      const c = parseColor(style)
      for (let k = 0; k < 4; k++) out[k] = c[k] * alpha
      return out
    }
    const g = style.geometry
    let t: number
    if (style.type === 'linear') {
      const [x0, y0] = this.tx(g[0], g[1])
      const [x1, y1] = this.tx(g[2], g[3])
      const dx = x1 - x0
      const dy = y1 - y0
      t = ((x - x0) * dx + (y - y0) * dy) / Math.max(dx * dx + dy * dy, 1e-9)
    } else {
      const [cx, cy] = this.tx(g[3], g[4])
      t = (Math.hypot(x - cx, y - cy) / this.scaleOf() - g[2]) / Math.max(g[5] - g[2], 1e-9)
    }
    return style.at(t, out, alpha)
  }

  private mode(): BlendMode {
    return this.globalCompositeOperation === 'lighter' ? 'add' : 'over'
  }

  /** How much the transform scales lengths (the square root of its determinant). */
  private scaleOf() {
    const m = this.m
    return Math.sqrt(Math.abs(m[0] * m[3] - m[1] * m[2])) || 1
  }

  private tx(x: number, y: number) {
    const m = this.m
    return [m[0] * x + m[2] * y + m[4], m[1] * x + m[3] * y + m[5]]
  }
}
